import { AREA_SWITCHES, SWITCH_INFO, type PermissionArea, type PermissionAreaInfo, type SwitchKey } from '@lib/constants/permissions'
import type { Grants } from '@lib/dto/tenant-role.dto'
import { Checkbox } from '@/components/ui/checkbox'
import { Radio } from '@/components/ui/radio'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { LEVEL_LABEL, PERMISSION_LEVELS, type PermissionLevel } from './roleText'

interface PermissionTableProps {
  heading: string
  areas: readonly PermissionAreaInfo[]
  grants: Grants
  onLevel: (key: PermissionArea, level: PermissionLevel) => void
  onSwitch: (key: SwitchKey, allowed: boolean) => void
}

/**
 * A role's permissions for a set of areas, one row each, with a column per
 * level: a grid read down as much as across, so a role's shape shows at a
 * glance ("everything at Edit but furniture"). Each row's radios share a name,
 * so arrow keys move along the row. A level an area doesn't have leaves its
 * cell empty rather than offering something that would be refused.
 */
export function PermissionTable({ heading, areas, grants, onLevel, onSwitch }: PermissionTableProps) {
  return (
    <Table heading={heading}>
      <TableHeader>
        <TableHead>Area</TableHead>
        {PERMISSION_LEVELS.map((level) => (
          <TableHead key={level} className="w-16 text-center">
            {LEVEL_LABEL[level]}
          </TableHead>
        ))}
      </TableHeader>
      <TableBody>
        {areas.map((area) => (
          <TableRow key={area.key}>
            <TableCell className="align-top">
              <p className="m-0 text-sm font-medium text-ink">{area.label}</p>
              <p className="m-0 mt-0.5 max-w-[60ch] text-small text-ink-muted">{area.description}</p>
              {(AREA_SWITCHES[area.key] ?? []).map((key) => (
                <Switch
                  key={key}
                  switchKey={key}
                  areaLabel={area.label}
                  allowed={grants[key]}
                  available={grants[area.key] === 'edit'}
                  onChange={(allowed) => onSwitch(key, allowed)}
                />
              ))}
            </TableCell>
            {PERMISSION_LEVELS.map((level) => (
              <TableCell key={level} className="text-center align-top">
                {level === 'none' || area.levels.includes(level) ? (
                  <Radio
                    name={`grant-${area.key}`}
                    checked={grants[area.key] === level}
                    onSelect={() => onLevel(area.key, level)}
                    aria-label={`${area.label}: ${LEVEL_LABEL[level]}`}
                    className="mt-0.5"
                  />
                ) : null}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface SwitchProps {
  switchKey: SwitchKey
  areaLabel: string
  allowed: boolean
  available: boolean
  onChange: (allowed: boolean) => void
}

/** A switch under its area: offered only with Edit, since each is a kind of editing. */
function Switch({ switchKey, areaLabel, allowed, available, onChange }: SwitchProps) {
  const info = SWITCH_INFO[switchKey]
  return (
    <label className="mt-2.5 flex items-start gap-2">
      <Checkbox checked={allowed} disabled={!available} onCheckedChange={onChange} className="mt-0.5" />
      <span>
        <span className={available ? 'block text-sm text-ink' : 'block text-sm text-ink-muted'}>{info.label}</span>
        <span className="block text-small text-ink-muted">{available ? info.description : `Needs Edit on ${areaLabel}.`}</span>
      </span>
    </label>
  )
}
